import { ChartCard, type ChartCardState } from "../../ChartCard";
import { UsageChart } from "../../UsageChart";
import type { TenantUsageHistory } from "../../../../hooks/useAdminStatus";
import type { AiMode } from "../../../../types/openclaw";
import { spendEmptyText } from "../../../../lib/aiSpend";

type SpendPanelProps = {
  readonly status: "loading" | "ready" | "error";
  readonly usage: TenantUsageHistory | null;
  readonly error: string | null;
  readonly aiMode?: AiMode | undefined;
};

const hasSpend = (usage: TenantUsageHistory | null): boolean =>
  usage != null && usage.points.some((p) => p.spentUsd != null);

const cardState = (status: SpendPanelProps["status"], usage: TenantUsageHistory | null): ChartCardState => {
  if (status === "loading") return "loading";
  if (status === "error") return "error";
  if (!hasSpend(usage)) return "empty";
  return "ready";
};

const summary = (usage: TenantUsageHistory | null): string | undefined => {
  if (usage?.latestSpentUsd == null) return undefined;
  const limit = usage.limitUsd != null ? ` of $${usage.limitUsd.toFixed(0)}` : " spent";
  const period = usage.periodLabel != null ? ` (${usage.periodLabel})` : "";
  const provider = usage.provider != null ? ` · ${usage.provider}` : "";
  return `$${usage.latestSpentUsd.toFixed(2)}${limit}${period}${provider}`;
};

export const SpendPanel = ({ status, usage, error, aiMode }: SpendPanelProps): JSX.Element => (
  <ChartCard
    title="AI spend"
    subtitle={summary(usage)}
    state={cardState(status, usage)}
    emptyText={spendEmptyText(aiMode)}
    hint="Sampled every 15 minutes for instances with AI configured. The headline is the running total for the billing period."
    errorText={error ?? undefined}
  >
    {usage != null && <UsageChart usage={usage} />}
  </ChartCard>
);
